import { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Calendar, Folder, Save, FileJson, Check, Edit2, Calculator } from 'lucide-react';
import { ProjectSnapshot } from '@/shared/model';
import { roundTo } from '@/shared/lib/number';

interface ProjectsViewProps {
    library: ProjectSnapshot[];
    onOpen: (project: ProjectSnapshot) => void;
    onDeleteRequest: (id: string | number) => void;
    onRename: (id: string | number) => void;
    onSaveCurrent: () => void;
    newProjectName: string;
    setNewProjectName: (name: string) => void;
    editingId: string | number | null;
    setEditingId: (id: string | number | null) => void;
    editName: string;
    setEditName: (name: string) => void;
    /**
     * SYNC-03 (UI-SPEC backstop row 8): true while login-time hydration
     * (useSyncStore.hydrating) is in flight. Defaults to false so every
     * existing call site and test keeps today's behaviour unchanged.
     * While true AND the library is empty, the empty-state card is
     * withheld — a second-device user must never be told their library
     * is empty while hydration is still finding out. No spinner/skeleton
     * is introduced; withholding the card is the honest alternative.
     */
    hydrating?: boolean;
}

export const ProjectsView: FC<ProjectsViewProps> = ({
    library,
    onOpen,
    onDeleteRequest,
    onRename,
    onSaveCurrent,
    newProjectName,
    setNewProjectName,
    editingId,
    setEditingId,
    editName,
    setEditName,
    hydrating = false
}) => {
    const { t } = useTranslation();

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, action: () => void) => {
        if (e.key === 'Enter') action();
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Hero / Save Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-8 text-white shadow-xl shadow-blue-200">
                <h2 className="text-2xl font-bold mb-2 tracking-tight">{t('projects.hero_title', 'Добро пожаловать!')}</h2>
                <p className="text-blue-100 mb-6 font-medium max-w-lg">
                    {t('projects.hero_subtitle', 'Создайте новый проект или сохраните текущий прогресс в библиотеку.')}
                </p>
                
                <div className="flex flex-col md:flex-row gap-3 max-w-xl bg-white/10 p-2 rounded-2xl backdrop-blur-sm border border-white/20">
                    <input 
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder={t('projects.new_project_placeholder', 'Название проекта...')}
                        className="flex-1 px-4 py-3 bg-transparent placeholder-blue-200 text-white font-bold outline-none"
                        onKeyDown={(e) => handleKeyDown(e, onSaveCurrent)}
                    />
                    <button 
                        onClick={onSaveCurrent}
                        className="px-6 py-3 bg-white text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all active:scale-95 flex items-center justify-center gap-2 shadow-sm"
                    >
                        <Save size={18}/> {t('projects.save_btn', 'Сохранить')}
                    </button>
                </div>
            </div>

            {/* Grid */}
            <div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Folder className="text-blue-500" size={24}/>
                    {t('projects.list_title', 'Мои проекты')}
                    <span className="text-slate-300 dark:text-slate-700 text-sm font-medium ml-2">{library.length}</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {library.length === 0 ? (
                        // UI-SPEC backstop row 8: while hydration is in flight, an empty
                        // library is not yet a settled answer — withhold the card rather
                        // than show a "library is empty" state hydration may still
                        // contradict. Nothing renders here (no spinner/skeleton).
                        !hydrating && (
                        <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-slate-300 dark:text-slate-700">
                                <Folder size={32}/>
                            </div>
                            <p className="text-slate-400 dark:text-slate-600 font-bold">{t('projects.empty_state', 'Библиотека пуста')}</p>
                        </div>
                        )
                    ) : (
                        library.map(project => (
                            <div key={project.id} className="group bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-900 hover:shadow-lg transition-all relative overflow-hidden">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center">
                                        <FileJson size={24}/>
                                    </div>
                                    <button 
                                        onClick={() => onDeleteRequest(project.id)}
                                        className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100"
                                    >
                                        <Trash2 size={18}/>
                                    </button>
                                </div>

                                {editingId === project.id ? (
                                    <div className="flex items-center gap-2 mb-2">
                                        <input 
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            className="flex-1 text-base font-bold border-b-2 border-blue-500 outline-none pb-1 bg-transparent dark:text-white"
                                            autoFocus
                                            onKeyDown={(e) => handleKeyDown(e, () => onRename(project.id))}
                                        />
                                        <button onClick={() => onRename(project.id)} className="text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded"><Check size={18}/></button>
                                    </div>
                                ) : (
                                    <div className="mb-2 group/title">
                                        <h4 className="font-bold text-slate-800 dark:text-slate-100 text-lg leading-tight line-clamp-1 flex items-center gap-2">
                                            {project.name}
                                            <button 
                                                onClick={() => { setEditingId(project.id); setEditName(project.name); }}
                                                className="opacity-0 group-hover/title:opacity-100 text-slate-300 dark:text-slate-600 hover:text-blue-500 transition-opacity"
                                            >
                                                <Edit2 size={14}/>
                                            </button>
                                        </h4>
                                    </div>
                                )}

                                <div className="flex gap-4 text-xs font-bold text-slate-400 dark:text-slate-500 mb-6">
                                    <span className="flex items-center gap-1.5"><Calendar size={14}/> <span className="font-mono tabular-nums">{new Date(project.lastModified).toLocaleDateString()}</span></span>
                                    <span className="flex items-center gap-1.5"><Calculator size={14}/> <span className="font-mono tabular-nums">{roundTo(project.stats?.total || 0, 0)}</span></span>
                                </div>

                                <button 
                                    onClick={() => onOpen(project)}
                                    className="w-full py-3 bg-slate-50 dark:bg-slate-800 hover:bg-blue-600 dark:hover:bg-blue-600 hover:text-white text-slate-600 dark:text-slate-400 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                                >
                                    {t('projects.open_btn', 'Открыть проект')}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
